import React, { useState } from 'react';
import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Container from '@mui/material/Container';
import Divider from '@mui/material/Divider';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import Folder from '@mui/icons-material/Folder';
import Save from '@mui/icons-material/Save';
import Share from '@mui/icons-material/Share';
import { withTranslation } from 'react-i18next';
import * as PropTypes from 'prop-types';
import './TitleBar.css';
import { useAuth } from '../AuthContext';
import LoadDialog from './TitleBarItems/LoadDialog';
import SaveDialog from './TitleBarItems/SaveDialog';
import ShareDialog from './TitleBarItems/ShareDialog';
import GraphicsLayer from '@arcgis/core/layers/GraphicsLayer';
import { sessionStatuses } from '../utils/Consts';
import SessionExpriredDialog from '../SessionExpriredDialog';

// TODO: Refactor TitleBar so it doesn't contain the "dialogs"; it just takes callbacks to open them:
function TitleBar({
  t,
  globalAssessmentState,
  graphicsLayer,
  setAssessmentId,
  setSavedAuthor,
  setAllFormState,
  canSave,
  onSaved
}) {
  const { email, authenticate, sessionState } = useAuth();
  const [timeoutWarningIgnored, setTimeoutWarningIgnored] = useState(false);

  const [existingAssessment, setExistingAssessment] = useState(false);

  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const timeoutWarningOpen = canSave && sessionState === sessionStatuses.ALMOST_EXPIRED && !timeoutWarningIgnored;
  const [loadDialogOpen, setLoadDialogOpen] = useState(false);

  const [shareDialogOpen, setShareDialogOpen] = useState(false);

  const [sharedUsers, setSharedUsers] = useState([]);

  const titlebarSx = {
    color: 'white',
    ':disabled': { color: 'rgba(255,255,255,0.26)' }
  };

  const divider = (
    <Divider sx={{ borderColor: 'rgba(255,255,255,.5)', margin: '0 1em' }}
      orientation='vertical'
      variant='middle'
      flexItem
    />
  );

  return (
    <AppBar position='static'>
      <Container maxWidth='xl'>
        <Toolbar disableGutters>
          <img
            src="/NatureServe-logo_H-white.png"
            alt="NatureServe"
            className="logo"
          />
          <Box sx={{ flexGrow: 1, display: { xs: 'none', md: 'flex', alignItems: 'center' } }}>
            <Typography
              variant='h6'
              component='h1'
              noWrap
              sx={{ fontSize: '1em' }}
            >
              {t('titleBar.assessmentForm')}
           </Typography>
            <SessionExpriredDialog />
            {email
              ? (
                <>
                  {divider}
                  <Button
                    onClick={() => setLoadDialogOpen(true)}
                    sx={titlebarSx}
                    startIcon={<Folder />}
                  >
                    {t('titleBar.load')}
                  </Button>
                  <LoadDialog
                    open={loadDialogOpen}
                    setOpen={setLoadDialogOpen}
                    setExistingAssessment={setExistingAssessment}
                    setAllFormState={setAllFormState}
                    setSharedUsers={setSharedUsers}
                    currentAssessmentId={globalAssessmentState.assessmentId}
                  />
                  {divider}
                  <Button
                    onClick={() => setSaveDialogOpen(true)}
                    sx={titlebarSx}
                    startIcon={<Save />}
                    disabled={!canSave}
                  >
                    {t('titleBar.save')}
                  </Button>
                  <SaveDialog
                    globalAssessmentState={globalAssessmentState}
                    graphicsLayer={graphicsLayer}
                    open={saveDialogOpen || timeoutWarningOpen}
                    onClose={() => {
                      setSaveDialogOpen(false);
                      if (timeoutWarningOpen) {
                        setTimeoutWarningIgnored(true);
                      }
                    }}
                    onSaved={onSaved}
                    setAssessmentId={setAssessmentId}
                    setSavedAuthor={setSavedAuthor}
                    existingAssessment={existingAssessment}
                    setExistingAssessment={setExistingAssessment}
                    setSharedUsers={setSharedUsers}
                  />
                  <Button
                    onClick={() => setShareDialogOpen(true)}
                    sx={titlebarSx}
                    startIcon={<Share />}
                    disabled={!existingAssessment}
                  >
                    {t('titleBar.share')}
                  </Button>
                  <ShareDialog
                    open={shareDialogOpen}
                    setOpen={setShareDialogOpen}
                    existingAssessment={existingAssessment}
                    sharedUsers={sharedUsers}
                    setSharedUsers={setSharedUsers}
                    author={globalAssessmentState.author}
                    assessmentId={globalAssessmentState.assessmentId}
                  />
                </>
              )
              : null}
          </Box>
          <Box sx={{ flexShrink: 1, display: { xs: 'none', md: 'flex' } }}>
            {email
              ? <Typography>{email}</Typography>
              : <Button
                onClick={() => { authenticate(); }}
                sx={titlebarSx}
              >
                {t('titleBar.login')}
              </Button>
            }
          </Box>
        </Toolbar>
      </Container>
    </AppBar>
  );
}

TitleBar.propTypes = {
  t: PropTypes.func.isRequired,
  setAssessmentId: PropTypes.func.isRequired,
  setSavedAuthor: PropTypes.func.isRequired,
  globalAssessmentState: PropTypes.shape({
    ccviVersion: PropTypes.string.isRequired,
    assessmentId: PropTypes.string.isRequired,
    author: PropTypes.string.isRequired,
    assessed: PropTypes.object.isRequired,
    surveyAnswers: PropTypes.objectOf(PropTypes.number).isRequired,
    exposureResults: PropTypes.object.isRequired,
    exposureStatus: PropTypes.string.isRequired
  }).isRequired,
  graphicsLayer: PropTypes.instanceOf(GraphicsLayer).isRequired,
  canSave: PropTypes.bool.isRequired,
  onSaved: PropTypes.func.isRequired
};

export default withTranslation('translations')(TitleBar);
